import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, ApiError, Button, Eyebrow, Field, Input } from '@busbook/ui';
import { useTenant } from '../lib/tenant.js';
import { useCustomer } from '../lib/customer-session.js';
import { api } from '../lib/api.js';
import { useT } from '../i18n/index.js';
import { safeNext } from '../lib/next-path.js';

/**
 * Customer accounts (docs/05 `/auth/*`). An account is a convenience — it keeps
 * your trips in one place — never a requirement, so every screen here says so and
 * offers the way back to booking as a guest.
 */

function AuthShell({
  eyebrow,
  title,
  lead,
  children,
  footer,
}: {
  eyebrow: string;
  title: string;
  lead?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <section className="px-4 py-8">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">{title}</h1>
      {lead && (
        <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
          {lead}
        </p>
      )}
      <div className="mt-6">{children}</div>
      {footer && <div className="mt-5 text-sm">{footer}</div>}
    </section>
  );
}

export function SignIn() {
  const t = useT();
  const { signIn } = useCustomer();
  const { branding } = useTenant();
  const navigate = useNavigate();
  // Where the passenger was headed when they were asked to sign in. Booking requires an
  // account now, so most people arrive here mid-task rather than on purpose.
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'AUTH_INVALID'
          ? 'Email or password is incorrect.'
          : 'We could not sign you in. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      eyebrow={t('a.yourAccount')}
      title={`Sign in to ${branding.appName}`}
      lead={t('a.signInLead')}
      footer={
        <div className="flex flex-col gap-2">
          {/* The destination travels with them: someone who registers instead of signing
              in must still land on the trip they were booking. */}
          <Link
            to={`/auth/register?next=${encodeURIComponent(next)}`}
            className="underline underline-offset-4"
            style={{ color: 'var(--color-primary)' }}
          >
            {t('a.createAccount')}
          </Link>
          <Link to="/auth/forgot" className="underline underline-offset-4" style={{ color: 'var(--color-muted)' }}>
            {t('a.forgot')}
          </Link>
          <Link to="/" className="underline underline-offset-4" style={{ color: 'var(--color-muted)' }}>
            {t('a.keepLooking')}
          </Link>
        </div>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t('c.email')} htmlFor="email">
          <Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={t('c.password')} htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {error && <Alert tone="error">{error}</Alert>}
        <Button type="submit" size="lg" block loading={busy}>
          {t('c.signIn')}
        </Button>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const t = useT();
  const { register } = useCustomer();
  const { branding } = useTenant();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await register(form);
      navigate(next, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'EMAIL_TAKEN'
          ? 'That email already has an account here. Try signing in.'
          : err instanceof ApiError
            ? err.message
            : 'We could not create your account. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      eyebrow={t('a.yourAccount')}
      title={`Create a ${branding.appName} account`}
      lead={t('a.registerLead')}
      footer={
        <Link
          to={`/auth/login?next=${encodeURIComponent(next)}`}
          className="underline underline-offset-4"
          style={{ color: 'var(--color-primary)' }}
        >
          {t('a.haveAccount')}
        </Link>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t('c.yourName')} htmlFor="name">
          <Input id="name" autoComplete="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label={t('c.email')} htmlFor="email">
          <Input id="email" type="email" autoComplete="username" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label={t('c.password')} htmlFor="password" hint={t('a.min10')}>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </Field>
        {error && <Alert tone="error">{error}</Alert>}
        <Button type="submit" size="lg" block loading={busy}>
          {t('a.create')}
        </Button>
      </form>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const t = useT();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.request('/public/auth/forgot-password', {
        method: 'POST',
        auth: false,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } finally {
      // The endpoint answers the same either way, so the UI does too — saying
      // more would reveal who holds an account.
      setSent(true);
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthShell eyebrow={t('a.yourAccount')} title={t('m.checkEmail')}>
        <Alert tone="success">
          {t('a.resetSent')}
        </Alert>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow={t('a.yourAccount')}
      title={t('a.reset')}
      lead="We will email you a link to choose a new one."
      footer={
        <Link to="/auth/login" className="underline underline-offset-4" style={{ color: 'var(--color-muted)' }}>
          {t('a.backToSignIn')}
        </Link>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t('c.email')} htmlFor="email">
          <Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Button type="submit" size="lg" block loading={busy}>
          {t('a.emailLink')}
        </Button>
      </form>
    </AuthShell>
  );
}

export function ResetPassword() {
  const t = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const token = params.get('token') ?? '';

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.request('/public/auth/reset-password', {
        method: 'POST',
        auth: false,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      navigate('/auth/login');
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'INVITE_INVALID'
          ? 'That link has expired or has already been used. Ask for a new one.'
          : 'We could not change your password. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthShell eyebrow={t('a.yourAccount')} title={t('a.reset')}>
        <Alert tone="error">
          {t('a.linkIncomplete')}
        </Alert>
      </AuthShell>
    );
  }

  return (
    <AuthShell eyebrow={t('a.yourAccount')} title={t('a.chooseNew')}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t('a.newPassword')} htmlFor="password" hint={t('a.min10')}>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {error && <Alert tone="error">{error}</Alert>}
        <Button type="submit" size="lg" block loading={busy}>
          {t('a.saveNew')}
        </Button>
        <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
          {t('a.signsOut')}
        </p>
      </form>
    </AuthShell>
  );
}
